import type { PentestFinding, PentestReportIndex } from '@/core/types/pentest.js';

export function buildPentestMarkdown(report: PentestReportIndex): string {
  const findings = report.findings as PentestFinding[];
  const summary = summarizeFindings(findings);
  const lines = [
    `# ${report.report_id}`,
    '',
    `- Workflow: \`${report.workflow}\``,
    `- Generated: \`${report.generated_at}\``,
    `- Stack: \`${report.stack.primary}\``,
    `- Traits: ${report.stack.traits.length > 0 ? report.stack.traits.map((trait) => `\`${trait}\``).join(', ') : 'none'}`,
    `- Runtime: \`${report.runtime.status}\`${report.runtime.target_url ? ` at \`${report.runtime.target_url}\`` : ''}`,
    report.source_report_path ? `- Source Report: \`${report.source_report_path}\`` : '',
    '',
    '## Methodology and Coverage',
    '',
    ...report.methodology.map((entry) => `- ${entry}`),
    '',
    `- Docs coverage: ${report.coverage.docs ? 'yes' : 'no'}`,
    `- Tests coverage: ${report.coverage.tests ? 'yes' : 'no'}`,
    `- Code coverage: ${report.coverage.code ? 'yes' : 'no'}`,
    `- Runtime coverage: ${report.coverage.runtime ? 'yes' : 'no'}`,
    `- Database coverage: ${report.coverage.database ? 'yes' : 'no'}`,
    '',
    '## Findings Summary',
    '',
    `- High: ${summary.high}`,
    `- Medium: ${summary.medium}`,
    `- Low: ${summary.low}`,
    '',
    '## Detailed Findings',
    '',
  ];

  for (const finding of report.findings) {
    lines.push(`### ${finding.id} — ${finding.title}`);
    lines.push('');
    lines.push(`- Impact: \`${finding.impact}\``);
    lines.push(`- Effort: \`${finding.effort}\``);
    lines.push(`- Status: \`${finding.status}\``);
    if ('retest_status' in finding) {
      lines.push(`- Retest: \`${finding.retest_status}\``);
    }
    lines.push(`- Confidence: \`${finding.confidence.toFixed(2)}\``);
    lines.push(`- Category: \`${finding.category}\``);
    lines.push('');
    lines.push('#### Description');
    lines.push('');
    lines.push(finding.description);
    lines.push('');
    lines.push('#### Possible Solution Direction');
    lines.push('');
    lines.push(finding.possible_solution_direction);
    lines.push('');
    lines.push('#### How To Reproduce');
    lines.push('');
    lines.push(...finding.how_to_reproduce.map((entry) => `- ${entry}`));
    lines.push('');
    lines.push('#### Impact Area');
    lines.push('');
    lines.push(...finding.impact_area.map((entry) => `- ${entry}`));
    lines.push('');
    lines.push('#### Evidence');
    lines.push('');
    lines.push(...finding.evidence.map((entry) => `- ${entry}`));
    lines.push('');
  }

  lines.push('## Blocked Checks / Coverage Gaps');
  lines.push('');
  if (report.blocked_checks.length === 0) {
    lines.push('- none');
  } else {
    lines.push(...report.blocked_checks.map((entry) => `- ${entry}`));
  }
  lines.push('');
  lines.push('## Sources Used');
  lines.push('');
  lines.push(...report.sources_used.map((entry) => `- ${entry}`));
  lines.push('');
  lines.push('## Next Remediation Priorities');
  lines.push('');
  lines.push(...report.next_remediation_priorities.map((entry) => `- ${entry}`));
  lines.push('');

  return `${lines.filter(Boolean).join('\n')}\n`;
}

export function remediationPriorities(findings: PentestFinding[]): string[] {
  const priorities = findings
    .slice()
    .sort((left, right) => severityRank(left.impact) - severityRank(right.impact))
    .slice(0, 5)
    .map((finding) => `${finding.id}: ${finding.title}`);

  return priorities.length > 0 ? priorities : ['No findings recorded.'];
}

function summarizeFindings(findings: PentestFinding[]): Record<'high' | 'medium' | 'low', number> {
  return findings.reduce(
    (summary, finding) => {
      summary[finding.impact] += 1;
      return summary;
    },
    { high: 0, medium: 0, low: 0 },
  );
}

function severityRank(impact: PentestFinding['impact']): number {
  if (impact === 'high') {
    return 0;
  }

  if (impact === 'medium') {
    return 1;
  }

  return 2;
}
